// src/components/SeriesEntrenamientoManager.js
// Pantalla de Horarios de entrenamiento (tabla series_entrenamiento)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Plus,
  Edit2,
  Power,
  ClipboardCheck,
  Trash2,
  CalendarDays,
  CalendarPlus,
  MoreHorizontal,
  Loader2,
  Clock,
  MapPin,
} from 'lucide-react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import FeaturedLayout from './layouts/FeaturedLayout';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import FormSerieEntrenamiento from './FormSerieEntrenamiento';
import GeneradorEventos from './GeneradorEventos';
import {
  getSeriesEntrenamiento,
  updateSerieEntrenamiento,
  deleteSerieEntrenamiento,
  getAllGroups,
} from '../lib/supabase';

const DIAS_STRIP = [
  { key: 'lunes', label: 'L', nombre: 'Lunes' },
  { key: 'martes', label: 'M', nombre: 'Martes' },
  { key: 'miercoles', label: 'X', nombre: 'Miércoles' },
  { key: 'jueves', label: 'J', nombre: 'Jueves' },
  { key: 'viernes', label: 'V', nombre: 'Viernes' },
  { key: 'sabado', label: 'S', nombre: 'Sábado' },
  { key: 'domingo', label: 'D', nombre: 'Domingo' },
];

const SerieCard = ({ serie, onEntrenar, onAgenda, onEditar, onGenerar, onToggleActivo, onEliminar }) => {
  const diasActivos = serie.dias_semana || [];
  return (
    <Card className={`h-full ${!serie.activo ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-semibold leading-tight">{serie.nombre}</CardTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {serie.grupos && (
                <CardDescription className="truncate">{serie.grupos.nombre_del_grupo}</CardDescription>
              )}
              <Badge variant={serie.activo ? 'default' : 'secondary'} className="shrink-0">
                {serie.activo ? 'Activo' : 'Inactivo'}
              </Badge>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 -mt-1 -mr-2 shrink-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditar(serie)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onGenerar(serie)}>
                <CalendarPlus className="h-4 w-4 mr-2" />
                Generar entrenamientos
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onToggleActivo(serie)}>
                <Power className="h-4 w-4 mr-2" />
                {serie.activo ? 'Desactivar' : 'Activar'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => onEliminar(serie)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Tira de días de la semana */}
        <div className="flex gap-1.5">
          {DIAS_STRIP.map((dia) => {
            const activo = diasActivos.includes(dia.key);
            return (
              <span
                key={dia.key}
                title={dia.nombre}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${
                  activo
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground/50'
                }`}
              >
                {dia.label}
              </span>
            );
          })}
        </div>

        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium">
              {serie.hora_inicio?.slice(0, 5)} – {serie.hora_fin?.slice(0, 5)}
            </span>
          </div>
          {serie.ubicacion && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
              <span>{serie.ubicacion}</span>
            </div>
          )}
        </div>

        {serie.descripcion && (
          <p className="text-sm text-muted-foreground line-clamp-2">{serie.descripcion}</p>
        )}
      </CardContent>

      <CardFooter className="flex gap-2 pt-0">
        <Button size="sm" onClick={() => onEntrenar(serie)} className="flex-1">
          <ClipboardCheck className="h-4 w-4 mr-1" />
          Entrenar
        </Button>
        <Button variant="outline" size="sm" onClick={() => onAgenda(serie)} className="flex-1">
          <CalendarDays className="h-4 w-4 mr-1" />
          Agenda
        </Button>
      </CardFooter>
    </Card>
  );
};

const SeriesEntrenamientoManager = () => {
  const navigate = useNavigate();
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();

  const [series, setSeries] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [showActivas, setShowActivas] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);
  const [serieToEdit, setSerieToEdit] = useState(null);
  const [showGeneradorModal, setShowGeneradorModal] = useState(false);
  const [serieToGenerate, setSerieToGenerate] = useState(null);

  const loadGrupos = useCallback(async () => {
    try {
      const { data, error } = await getAllGroups();
      if (error) throw error;
      setGrupos(data || []);
    } catch (error) {
      console.error('Error cargando grupos:', error);
      await alert('Error', 'No se pudieron cargar los grupos.');
    }
  }, [alert]);

  const loadSeries = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await getSeriesEntrenamiento(
        selectedGrupo?.id || null,
        showActivas ? true : null
      );

      if (error) throw error;
      setSeries(data || []);
    } catch (error) {
      console.error('Error cargando horarios:', error);
      await alert('Error', 'No se pudieron cargar los horarios de entrenamiento.');
    } finally {
      setLoading(false);
    }
  }, [selectedGrupo, showActivas, alert]);

  useEffect(() => {
    loadGrupos();
  }, [loadGrupos]);

  // loadSeries no depende de grupos (consulta series, opcionalmente filtradas por
  // selectedGrupo). No debe bloquearse tras grupos.length: si la org no tiene
  // grupos (o getAllGroups falla), el spinner quedaba infinito.
  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const handleNuevaSerie = () => {
    setSerieToEdit(null);
    setShowFormModal(true);
  };

  const handleEditarSerie = (serie) => {
    setSerieToEdit(serie);
    setShowFormModal(true);
  };

  const handleToggleActivo = async (serie) => {
    const nuevoEstado = !serie.activo;
    const titulo = nuevoEstado ? 'Activar horario' : 'Desactivar horario';
    const mensaje = nuevoEstado
      ? `¿Activar el horario "${serie.nombre}"?`
      : `¿Desactivar el horario "${serie.nombre}"? Los entrenamientos futuros de este horario se cancelarán.`;

    if (!(await confirm(titulo, mensaje, { destructive: !nuevoEstado }))) return;

    try {
      const { error } = await updateSerieEntrenamiento(serie.id, { activo: nuevoEstado });
      if (error) throw error;
      loadSeries();
    } catch (error) {
      console.error('Error actualizando horario:', error);
      await alert('Error', `No se pudo ${nuevoEstado ? 'activar' : 'desactivar'} el horario.`);
    }
  };

  const handleEliminarSerie = async (serie) => {
    if (
      !(await confirm(
        'Eliminar horario',
        `¿Eliminar "${serie.nombre}"? El horario se desactivará y dejará de generar entrenamientos.`,
        { destructive: true }
      ))
    )
      return;

    try {
      const { error } = await deleteSerieEntrenamiento(serie.id);
      if (error) throw error;
      loadSeries();
    } catch (error) {
      console.error('Error eliminando horario:', error);
      await alert('Error', 'No se pudo eliminar el horario.');
    }
  };

  const handleGenerarEventos = (serie) => {
    setSerieToGenerate(serie);
    setShowGeneradorModal(true);
  };

  const handleVerAgenda = (serie) => {
    // CalendarOverview espera groupIds como un array
    navigate('/agenda', { state: { groupIds: [serie.grupo_id] } });
  };

  const handleEntrenar = async (serie) => {
    if (serie.grupo_id) {
      navigate(`/attendance/${serie.grupo_id}`);
    } else {
      await alert('Error', 'No se puede tomar asistencia: grupo no encontrado.');
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setSerieToEdit(null);
    loadSeries();
  };

  const handleGeneradorSuccess = () => {
    setShowGeneradorModal(false);
    setSerieToGenerate(null);
    loadSeries();
  };

  return (
    <FeaturedLayout
      title="Horarios de entrenamiento"
      subtitle="Define los días y horas en que entrena cada grupo. La asistencia y la agenda usan estos horarios."
      refreshAction={loadSeries}
    >
      {/* Toolbar: filtros + acción principal */}
      <div className="mb-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Tabs
            value={showActivas ? 'activos' : 'todos'}
            onValueChange={(value) => setShowActivas(value === 'activos')}
          >
            <TabsList>
              <TabsTrigger value="activos">Activos</TabsTrigger>
              <TabsTrigger value="todos">Todos</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={handleNuevaSerie} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo horario
          </Button>
        </div>
        {grupos.length > 1 && (
          <Select
            value={selectedGrupo?.id || 'todos'}
            onValueChange={(value) => {
              setSelectedGrupo(value === 'todos' ? null : grupos.find((g) => g.id === value));
            }}
          >
            <SelectTrigger className="w-full sm:w-72">
              <SelectValue placeholder="Filtrar por grupo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los grupos</SelectItem>
              {grupos.map((grupo) => (
                <SelectItem key={grupo.id} value={grupo.id}>
                  {grupo.nombre_del_grupo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Grid de horarios */}
      {loading ? (
        <div className="flex items-center justify-center py-32 text-muted-foreground">
          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          Cargando horarios...
        </div>
      ) : series.length === 0 ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-center px-6 max-w-md">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <Calendar className="h-10 w-10 text-muted-foreground" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-foreground">Sin horarios de entrenamiento</h3>
            <p className="mb-6 text-muted-foreground">
              Un horario define los días y horas en que entrena un grupo. Con un horario,
              la asistencia toma la hora correcta y puedes llenar la agenda de entrenamientos.
            </p>
            <Button onClick={handleNuevaSerie}>
              <Plus className="h-4 w-4 mr-2" />
              Crear horario
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {series.map((serie) => (
            <SerieCard
              key={serie.id}
              serie={serie}
              onEntrenar={handleEntrenar}
              onAgenda={handleVerAgenda}
              onEditar={handleEditarSerie}
              onGenerar={handleGenerarEventos}
              onToggleActivo={handleToggleActivo}
              onEliminar={handleEliminarSerie}
            />
          ))}
        </div>
      )}

      {/* Modal para crear/editar horario */}
      <FormSerieEntrenamiento
        open={showFormModal}
        onOpenChange={(isOpen) => {
          setShowFormModal(isOpen);
          if (!isOpen) {
            setSerieToEdit(null);
          }
        }}
        serie={serieToEdit}
        grupos={grupos}
        onSuccess={handleFormSuccess}
      />

      {/* Modal para generar entrenamientos */}
      {showGeneradorModal && serieToGenerate && (
        <GeneradorEventos
          serie={serieToGenerate}
          isOpen={showGeneradorModal}
          onClose={() => {
            setShowGeneradorModal(false);
            setSerieToGenerate(null);
          }}
          onSuccess={handleGeneradorSuccess}
        />
      )}

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default SeriesEntrenamientoManager;
